import React, { useMemo } from "react";
import { Dialog, Typography, IconButton, Box, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useQuery } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";
import LoadingModal from "./loading-modal";
import { getRequest } from "@/utils/request";

interface VoucherInformationModalProps {
  open: boolean;
  onClose: () => void;
  lang: string;
  uuid: string;
}
interface Item {
  title: string;
  value: string | null;
}
interface Tab {
  title: string;
  items: Item[];
}
const VoucherInformationModal: React.FC<VoucherInformationModalProps> = ({
  open,
  onClose,
  lang,
  uuid,
}) => {
  const { i18n } = useLingui();
  const { data, isLoading } = useQuery({
    queryKey: ["voucherInformation", uuid],
    queryFn: () =>
      getRequest({
        path: { name: "voucherInformation", id: `uuid/${uuid}` },
        lang,
        skipCache: true,
      }),

    enabled: open,
  });
  const tabs = useMemo(() => data?.data?.tabs || [], [data]);

  return isLoading ? (
    <LoadingModal handleClose={onClose} />
  ) : (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{ p: "20px" }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "333px",
          m: 0,
        },
      }}
    >
      <Box sx={{ pt: "14px", pl: "25px", pr: "25px", pb: "25px", position: "relative" }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          data-testid="close-button"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "inherit",
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box>
          {tabs.map((tab: Tab, tabIndex: number) => (
            <Box key={uuidv4()} sx={{ mb: "20px" }}>
              <Typography sx={{ fontWeight: "600", fontSize: "21px" }}>
                {t(i18n)`${tab.title}`}
              </Typography>
              <Divider sx={{ mb: "20px", mt: "10px" }} />
              {tab.items?.map(
                (item: Item) =>
                  item.value && (
                    <Box key={uuidv4()} sx={{ mt: "10px" }}>
                      {item.title && (
                        <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                          {item.title}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: "14px", overflowWrap: "break-word" }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ),
              )}
              {tabIndex < tabs.length - 1 && <br />}
            </Box>
          ))}
        </Box>
      </Box>
    </Dialog>
  );
};
export default VoucherInformationModal;
